import "./pagination.scss";

import cn from "clsx";
import { MouseEvent } from "react";
import { Link } from "design-system/components/primitives/link/link";
import { PaginationNumber } from "./pagination-number";
import { Icon } from "design-system/components/icons/icon";
import { SimpleTheme } from "design-system/utils/theme";

const LARGE_PAGE_COUNT = 8;

export interface Page {
  isCurrent?: boolean;
  link: string;
  number: number;
}

export type HandlePageClick = (
  e: MouseEvent<HTMLAnchorElement>,
  page: Page,
) => void;

export interface PaginationProps {
  pages: Array<Page>;
  size?: number;
  theme?: SimpleTheme;
  onPageClick?: (page: Page) => void;
}

/**
 * ## See it in use on...
 * - At the bottom of an [archive page](/story/example-pages-archive-pages-story-archive--story)
 *
 * - **`id: P-002-000-00`**
 */
export function Pagination({
  pages,
  size = 10,
  theme,
  onPageClick,
}: PaginationProps) {
  const maxPages = Math.floor(10000 / size);
  const cappedPages =
    pages.length * size > 10000 ? pages.slice(0, maxPages) : pages;

  const currentPage = cappedPages.find((page) => page.isCurrent);
  if (cappedPages.length <= 1 || !currentPage) return null;

  const currentPageNumber = currentPage.number;

  const pageGrouping =
    currentPageNumber < 5
      ? "first"
      : currentPageNumber > cappedPages.length - 4
        ? "last"
        : "center";

  const firstGroup = pageGrouping === "first" ? 5 : 1;
  const lastGroup = pageGrouping === "last" ? 5 : 1;

  let firstPages = cappedPages.slice(0, firstGroup);
  const lastPages = cappedPages.slice(
    cappedPages.length - lastGroup,
    cappedPages.length,
  );
  let centerPages: Array<Page> | undefined;

  if (
    cappedPages.length > LARGE_PAGE_COUNT &&
    currentPageNumber > cappedPages.length - 5 &&
    currentPageNumber < cappedPages.length - 5
  ) {
    firstPages = cappedPages.slice(
      cappedPages.length - 6,
      cappedPages.length - 3,
    );
  }

  if (currentPageNumber > 4 && currentPageNumber < cappedPages.length - 3) {
    centerPages = cappedPages.slice(
      currentPageNumber - 3,
      currentPageNumber + 2,
    );
  }

  const currentIndex = cappedPages.indexOf(currentPage);
  const isFirstPage = currentIndex === 0;
  const isLastPage = currentIndex === cappedPages.length - 1;
  const prevPage = !isFirstPage ? cappedPages[currentIndex - 1] : undefined;
  const nextPage = !isLastPage ? cappedPages[currentIndex + 1] : undefined;

  const handlePageClick: HandlePageClick = (e, page) => {
    e.preventDefault();
    if (onPageClick) onPageClick(page);
  };

  return (
    <div data-theme={theme}>
      <nav className="hbs-pagination" aria-label="Pagination">
        <div className="hbs-pagination__content">
          {/* Pages */}
          <ul className="hbs-pagination__pages">
            {cappedPages.length <= LARGE_PAGE_COUNT ? (
              cappedPages && (
                <PaginationNumber
                  pages={cappedPages}
                  handlePageClick={handlePageClick}
                />
              )
            ) : (
              <>
                {firstPages && (
                  <PaginationNumber
                    pages={firstPages}
                    handlePageClick={handlePageClick}
                  />
                )}
                <li>
                  <Icon name="ellipsis" className="hbs-pagination__ellipsis" />
                </li>
                {centerPages && (
                  <PaginationNumber
                    pages={centerPages}
                    handlePageClick={handlePageClick}
                  />
                )}
                {centerPages && (
                  <li>
                    <Icon
                      name="ellipsis"
                      className="hbs-pagination__ellipsis"
                    />
                  </li>
                )}
                {lastPages && (
                  <PaginationNumber
                    pages={lastPages}
                    handlePageClick={handlePageClick}
                  />
                )}
              </>
            )}
          </ul>

          {/* Controls */}
          {cappedPages.length > 1 && (
            <ul className="hbs-pagination__controls">
              <li
                className={cn(
                  "hbs-pagination__control-item",
                  isFirstPage && "hbs-pagination__control-item--disabled",
                )}
              >
                {prevPage ? (
                  <Link
                    className="hbs-pagination__link"
                    href={prevPage.link}
                    aria-label="Go to previous page"
                    onClick={(evt) => handlePageClick(evt, prevPage)}
                  >
                    Previous
                  </Link>
                ) : (
                  <span className="hbs-pagination__link">Previous</span>
                )}
              </li>
              <span
                aria-hidden="true"
                className="hbs-pagination__divider"
              ></span>
              <li
                className={cn(
                  "hbs-pagination__control-item",
                  isLastPage && "hbs-pagination__control-item--disabled",
                )}
              >
                {nextPage ? (
                  <Link
                    className="hbs-pagination__link"
                    href={nextPage.link}
                    aria-label="Go to next page"
                    onClick={(evt) => handlePageClick(evt, nextPage)}
                  >
                    Next
                  </Link>
                ) : (
                  <span className="hbs-pagination__link">Next</span>
                )}
              </li>
            </ul>
          )}
        </div>
      </nav>
    </div>
  );
}
